import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Gem, Clock, FileText, HardHat, Sparkles, User } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { FinalCta } from "@/components/home/Sections";
import { SITE } from "@/config/site";

const VALUES = [
  { icon: Gem, t: "Qualité d'exécution", d: "Des gestes précis, des matériaux adaptés et des finitions soignées, sur chaque chantier." },
  { icon: Clock, t: "Respect des délais", d: "Un planning annoncé et tenu. En cas d'aléa, vous êtes informé immédiatement." },
  { icon: FileText, t: "Transparence des devis", d: "Des devis détaillés poste par poste, sans frais cachés, valables 30 jours." },
  { icon: HardHat, t: "Sécurité sur chantier", d: "Protection des personnes et des lieux, respect des règles de sécurité." },
  { icon: Sparkles, t: "Propreté et finitions", d: "Un chantier propre chaque soir et une réception sans mauvaise surprise." },
];

export default function APropos() {
  const rows = [
    ["Dénomination", SITE.name],
    ["Forme juridique", SITE.legalForm],
    ["Capital social", SITE.capital],
    ["Siège social", SITE.address.full],
    ["RCS", SITE.rcs],
    ["SIREN / SIRET", `${SITE.siren} / ${SITE.siret}`],
    ["EUID", SITE.euid],
    ["TVA intracommunautaire", SITE.tva],
    ["Code APE / NAF", `${SITE.ape} — ${SITE.apeLabel}`],
    ["Date d'immatriculation", SITE.registrationDate],
    ["Début d'activité", SITE.activityStartDate],
    ["Exercice social", `Clôture au ${SITE.fiscalYearEnd} (premier exercice : ${SITE.firstFiscalYearEnd})`],
    ["Dirigeant", `${SITE.president.fullName}, ${SITE.president.title}`],
    ["Qualité d'artisan", `Entreprise artisanale inscrite auprès de la Chambre des Métiers et de l'Artisanat (${SITE.cmaDate})`],
  ];

  return (
    <>
      <Seo
        title="À propos — O'RIZON 360, artisan du bâtiment à Nogent-sur-Marne"
        description="O'RIZON 360, SASU artisanale immatriculée au RCS de Créteil, spécialisée dans la rénovation et l'entretien d'immeubles en Île-de-France. Valeurs, assurances, fiche d'identité."
      />
      <PageHero crumb="À propos" eyebrow="À propos" lines={["Une entreprise artisanale,", "un engagement personnel"]} intro={`${SITE.name} est une ${SITE.legalFormShort} au capital de ${SITE.capital}, dont le siège est situé ${SITE.address.full}. Immatriculée au RCS de ${SITE.rcsCity} le ${SITE.registrationDate}, elle a débuté son activité le ${SITE.activityStartDate}.`} />

      <section className="section bg-concrete" data-testid="about-positioning">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            {/* EMPLACEMENT RÉSERVÉ — déposer ici une photo réelle du dirigeant / de l'équipe (ex. /images/equipe.jpg). */}
            <div className="relative rounded-3xl bg-slate aspect-[4/5] flex flex-col items-center justify-center text-white/70 grain overflow-hidden" data-testid="portrait-placeholder">
              <User size={72} strokeWidth={1} className="text-copper relative z-10" />
              <p className="mt-4 text-sm font-semibold relative z-10">Portrait à venir</p>
              <p className="text-xs text-white/50 relative z-10">Emplacement réservé pour une photo réelle</p>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow mb-5">Mot du Président</p>
              <h2 className="h-display text-4xl sm:text-5xl">Un travail soigné, <span className="text-copper">des engagements tenus</span></h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 space-y-5 text-ink/85 leading-relaxed text-base sm:text-lg">
              <p>« J'ai créé O'RIZON 360 avec une conviction simple : un chantier réussi repose sur un travail bien fait, des délais respectés et une relation directe avec le client.</p>
              <p>Nous intervenons sur des immeubles et des logements en Île-de-France, auprès des particuliers comme des professionnels, des syndics et des bailleurs. Chaque projet est suivi de bout en bout par un interlocuteur unique, du premier échange à la réception des travaux.</p>
              <p>Notre taille est un atout : proximité, réactivité et responsabilité personnelle sur chaque intervention. »</p>
              <p className="font-heading font-bold text-xl text-slate pt-2" data-testid="president-signature">{SITE.president.fullName}, {SITE.president.title}</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section bg-white" data-testid="values-section">
        <div className="container-x">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow mb-5">Nos valeurs</p>
            <h2 className="h-display text-4xl sm:text-5xl">Ce qui guide <span className="text-copper">chaque intervention</span></h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {VALUES.map(({ icon: Icon, t, d }) => (
              <StaggerItem key={t} className="card card-hover p-6">
                <Icon className="text-copper" size={28} strokeWidth={1.75} />
                <h3 className="font-heading font-bold uppercase text-xl text-slate mt-5">{t}</h3>
                <p className="mt-3 text-sm text-muted leading-relaxed">{d}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="section bg-concrete" data-testid="identity-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow mb-5">Fiche d'identité</p>
            <h2 className="h-display text-4xl sm:text-5xl">L'entreprise <span className="text-copper">en clair</span></h2>
            <div className="mt-8 card p-6" data-testid="insurance-block">
              <ShieldCheck className="text-copper" size={28} />
              <h3 className="font-heading font-bold uppercase text-xl text-slate mt-4">Assurances</h3>
              <p className="mt-3 text-sm text-ink/85 leading-relaxed">
                {SITE.name} est couverte par une assurance responsabilité civile professionnelle et une assurance décennale souscrites auprès de <strong>{SITE.insurance.insurer}</strong>{SITE.insurance.toConfirm && " (à confirmer)"}, couvrant la {SITE.insurance.coverage}.
              </p>
              <p className="mt-2 text-sm font-semibold text-copper">{SITE.insurance.note}</p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="card overflow-hidden">
              <table className="w-full text-sm sm:text-base" data-testid="identity-table">
                <tbody className="divide-y divide-line">
                  {rows.map(([k, v]) => (
                    <tr key={k} className="hover:bg-concrete/60 transition-colors">
                      <th scope="row" className="text-left font-semibold text-slate px-5 py-4 w-1/3 align-top">{k}</th>
                      <td className="px-5 py-4 text-ink/85">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Link to="/contact" className="btn-primary mt-8" data-testid="about-devis-cta">Demander un devis gratuit <ArrowRight size={18} /></Link>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
